/**
 * Where each item of a collage sits in the frame.
 *
 * Pure geometry, in fractions of the frame, resolved to pixels by the caller.
 * That split matters: the editor draws a preview at one size and the export
 * composites at another, and a layout computed twice from different pixel
 * dimensions rounds differently. Fractions computed once and scaled cannot.
 *
 * Every layout is a hand-checked arrangement rather than a generic grid, because
 * a generic grid gives four items a nice 2×2 and three items a hole. The set is
 * closed for the same reason it is in `CollageLayout`.
 */
import { COLLAGE_CAPACITY, type CollageLayout } from "./types";

/** A rectangle in FRACTIONS of the frame: 0..1 on both axes. */
export interface FractionRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The same rectangle in pixels. */
export interface PixelRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * The gutter between cells, as a fraction of the frame's SHORTER side.
 *
 * Shorter side rather than per-axis, and that distinction is the whole reason
 * `layoutCells` takes an aspect: one fraction applied to both axes of a
 * 1080×1920 frame is a 6-pixel gap horizontally and an 11-pixel gap vertically,
 * which reads as a misaligned grid rather than a gutter. Deriving both from the
 * shorter side keeps the gap visually square at any frame shape.
 */
export const COLLAGE_GUTTER = 0.006;

/**
 * The gutter as a fraction of each axis, for a frame of this aspect.
 *
 * `aspect` is width / height. A square frame gives the same fraction on both
 * axes; anything else shrinks the fraction on the LONGER axis so the pixel gap
 * matches.
 */
export function gutterFor(aspect: number): { gx: number; gy: number } {
  const safe = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
  return {
    gx: COLLAGE_GUTTER * Math.min(1, 1 / safe),
    gy: COLLAGE_GUTTER * Math.min(1, safe),
  };
}

/**
 * The cells of a layout, in reading order.
 *
 * Reading order is `clipIds` order, so swapping two photos in a grid is a
 * reorder of the element's media rather than a different layout.
 *
 * `aspect` (width / height) shapes the gutters only; the cells themselves are
 * fractions and scale to any frame. It defaults to square, which is the shape
 * where both axes share one fraction.
 */
export function layoutCells(layout: CollageLayout, aspect = 1): FractionRect[] {
  const { gx, gy } = gutterFor(aspect);
  const halfW = (1 - gx) / 2;
  const halfH = (1 - gy) / 2;

  switch (layout) {
    case "single":
      return [{ x: 0, y: 0, width: 1, height: 1 }];

    case "side-by-side":
      return [
        { x: 0, y: 0, width: halfW, height: 1 },
        { x: halfW + gx, y: 0, width: halfW, height: 1 },
      ];

    case "stacked":
      return [
        { x: 0, y: 0, width: 1, height: halfH },
        { x: 0, y: halfH + gy, width: 1, height: halfH },
      ];

    // The second item INSET over the first. The overlap is the point — a
    // talking head over a screen recording, a reaction over a shot — so unlike
    // every other layout these two cells are not disjoint, and the second is
    // drawn last because it is on top. Inset by the gutter on both sides so it
    // does not touch the frame edge.
    //
    // Two corners, not one with a flag: which corner is free depends on what is
    // in the shot behind it, and a layout is the one thing in this model that is
    // chosen by looking at a picture. Both are the same geometry mirrored
    // vertically.
    case "pip":
    case "pip-top": {
      const insetW = 0.3;
      const insetH = 0.3;
      const x = 1 - insetW - gx * 2;
      const y = layout === "pip-top" ? gy * 2 : 1 - insetH - gy * 2;
      return [
        { x: 0, y: 0, width: 1, height: 1 },
        { x, y, width: insetW, height: insetH },
      ];
    }

    case "grid-2x2":
      return [
        { x: 0, y: 0, width: halfW, height: halfH },
        { x: halfW + gx, y: 0, width: halfW, height: halfH },
        { x: 0, y: halfH + gy, width: halfW, height: halfH },
        { x: halfW + gx, y: halfH + gy, width: halfW, height: halfH },
      ];

    // One tall item on the left, two stacked on the right. Reads better than a
    // 3-across strip on a portrait frame, where three columns are slivers.
    case "grid-3-left":
      return [
        { x: 0, y: 0, width: halfW, height: 1 },
        { x: halfW + gx, y: 0, width: halfW, height: halfH },
        { x: halfW + gx, y: halfH + gy, width: halfW, height: halfH },
      ];

    // A wide hero across the top, two beneath it.
    case "feature-top":
      return [
        { x: 0, y: 0, width: 1, height: halfH },
        { x: 0, y: halfH + gy, width: halfW, height: halfH },
        { x: halfW + gx, y: halfH + gy, width: halfW, height: halfH },
      ];
  }
  // Exhaustive above; this is the guard for a layout read from storage that a
  // newer build wrote. Falling back to `single` shows the first item rather
  // than nothing.
  return [{ x: 0, y: 0, width: 1, height: 1 }];
}

/**
 * The cells actually used for `count` items, in pixels.
 *
 * When there are FEWER items than cells the layout is downgraded rather than
 * left with holes: three photos in a 2×2 is a gap, and a gap reads as a bug. The
 * downgrade picks the layout whose capacity matches, which is what
 * `defaultLayoutFor` would have chosen for that count.
 *
 * When there are MORE items than cells the extras are dropped — the element's
 * layout is the operator's stated intent, and silently inventing cells would
 * change the composition they chose.
 */
export function resolveCollage(
  layout: CollageLayout | undefined,
  count: number,
  frame: { width: number; height: number },
): PixelRect[] {
  const requested = layout ?? "single";
  const effective =
    COLLAGE_CAPACITY[requested] > count ? downgradeFor(count) : requested;
  const cells = layoutCells(effective, frame.width / frame.height).slice(
    0,
    Math.max(0, count),
  );
  // Gutters are already inside the fractions, so only the rects are scaled.
  return cells.map((cell) => ({
    x: Math.round(cell.x * frame.width),
    y: Math.round(cell.y * frame.height),
    // Rounded from the far EDGE rather than by scaling the width, so adjacent
    // cells meet exactly and a rounding error cannot leave a one-pixel seam of
    // background between them.
    width:
      Math.round((cell.x + cell.width) * frame.width) -
      Math.round(cell.x * frame.width),
    height:
      Math.round((cell.y + cell.height) * frame.height) -
      Math.round(cell.y * frame.height),
  }));
}

/** The layout that fills `count` cells exactly. */
function downgradeFor(count: number): CollageLayout {
  if (count <= 1) return "single";
  if (count === 2) return "side-by-side";
  if (count === 3) return "grid-3-left";
  return "grid-2x2";
}

/**
 * Where to draw a source image inside a cell so it FILLS it without distorting.
 *
 * Cover, not contain: a collage of letterboxed photos each with their own bars
 * is a mess, and cropping a little off a photo is what every collage does. The
 * crop is centred, which is the only choice that needs no per-item state.
 *
 * Returns the SOURCE rectangle to sample — the destination is the cell — so the
 * caller passes both to one `drawImage`.
 */
export function coverCrop(
  source: { width: number; height: number },
  cell: { width: number; height: number },
): { sx: number; sy: number; sWidth: number; sHeight: number } {
  if (
    source.width <= 0 ||
    source.height <= 0 ||
    cell.width <= 0 ||
    cell.height <= 0
  ) {
    return {
      sx: 0,
      sy: 0,
      sWidth: Math.max(1, source.width),
      sHeight: Math.max(1, source.height),
    };
  }
  const sourceAspect = source.width / source.height;
  const cellAspect = cell.width / cell.height;

  if (sourceAspect > cellAspect) {
    // Source is wider: take a full-height slice and crop the sides.
    const sWidth = source.height * cellAspect;
    return {
      sx: (source.width - sWidth) / 2,
      sy: 0,
      sWidth,
      sHeight: source.height,
    };
  }
  // Source is taller: take a full-width slice and crop top and bottom.
  const sHeight = source.width / cellAspect;
  return {
    sx: 0,
    sy: (source.height - sHeight) / 2,
    sWidth: source.width,
    sHeight,
  };
}
